import {
  drop,
  flat,
  fromEntries,
  isIncludedIn,
  isNonNullish,
  isNullish,
  map,
  omit,
  partition,
  pipe,
  piped,
  prop,
  zip,
} from 'remeda';
import type { CompanyConfiguration } from 'shared/hooks/use-load-company-configuration';
import type { DebtorImportColumns, InvoiceImportColumns } from 'types/store/app-state';

import type { CsvData, MatchedColumns } from './types.ts';

export function computeFields(
  columns: InvoiceImportColumns | DebtorImportColumns,
  matchedColumns: MatchedColumns,
) {
  const { fields, required_fields: requiredFields } = columns;

  const allFields = fields.map(({ value, description }) => ({ value, label: description }));

  const [mandatoryFields, optionalFields] = partition(
    allFields,
    piped(prop('value'), isIncludedIn(flat(requiredFields))),
  );

  type Field = (typeof allFields)[number];
  const cleanMatchedColumns = matchedColumns.filter(isNonNullish);
  const isUnmatched = ({ value }: Field) => !isIncludedIn(value, cleanMatchedColumns);
  const unmatchedMandatoryFields = mandatoryFields.filter(isUnmatched);
  const unmatchedOptionalFields = optionalFields.filter(isUnmatched);

  return {
    allFields,
    optionalFields,
    unmatchedOptionalFields,
    mandatoryFields,
    unmatchedMandatoryFields,
  };
}

export function computeShouldShowForceBalanceUpdateCheckbox(
  isInvoiceMode: boolean,
  matchedColumns: MatchedColumns,
  company: CompanyConfiguration,
) {
  if (!isInvoiceMode) return false;

  const isRemainingBalancePresent = matchedColumns.includes('remaining_balance');
  const hasBankingIntegration = (() => {
    if (company.codabox_integration.active) return true;
    if (company.ponto_integration.active) return true;
    if (company.digiteal_integration.status === 'activated') return true;

    return false;
  })();

  return isRemainingBalancePresent && hasBankingIntegration;
}
/**
 * Backend expects an array of objects, where the keys are the matched columns (repeated for each row).
 */
export function applyMatchedColumnsToData(data: CsvData, matchedColumns: MatchedColumns) {
  return pipe(
    data,
    drop(1), // Skip the header row
    map(row =>
      pipe(
        matchedColumns,
        // Preserve the gaps which represent unmatched columns, but turn nulls into strings so the zip function can work
        map(colName => (isNullish(colName) ? '_unmatched' : colName)),
        zip(row),
        fromEntries(),
        omit(['_unmatched']),
      ),
    ),
  );
}
